// API client for the spec pages (inventory, device detail, alarms, recording,
// firmware, topology, ai-health, provider, executive). {data} envelope.
import { api } from './api';

const get = <T>(path: string) => async (): Promise<T> => (await api.get<{ data: T }>(path)).data.data;

export interface InventoryDevice {
  id: string; name: string; type: string; vendor: string; model: string; ip: string;
  site: string; firmware: string; status: string; lastSeen: string | null; healthScore: number; customer: string;
  // Hierarchy + placement (B++): coreType ('nvr'/'camera'/...), parent (for
  // NVR→channel nesting), channel number, owning driver, and physical placement.
  coreType?: string;
  parentDeviceId?: string | null;
  channel?: number | null;
  driverInstanceId?: string | null;
  // Where this device is polled from: edgeId set ⇒ that edge agent; null with a
  // driverInstanceId ⇒ cloud-direct; both null ⇒ manually added (nothing polls it).
  edgeId?: string | null;
  driverInstanceName?: string | null;
  ptz?: boolean;
  monitored?: boolean;
  externalId?: string | null; externalSource?: string | null; replacedByDeviceId?: string | null;
  serialNumber?: string | null;
  // Spatial: geo (GIS map) + indoorX/Y normalized 0–1 (floor-plan twin).
  latitude?: number | null;
  longitude?: number | null;
  indoorX?: number | null;
  indoorY?: number | null;
  placement?: { organizationId: string | null; siteId: string | null; buildingId: string | null; floorId: string | null; zoneId: string | null };
}
export interface DeviceRefLite { id: string; name: string; coreType: string; channel: number | null; status: string; healthScore: number }
export interface DeviceDetail {
  id: string; name: string; serialNumber: string; vendor: string; model: string; firmware: string;
  site: string; customer?: string; installDate: string; healthScore: number; status: string;
  coreType?: string; channel?: number | null; lastProbedAt?: string | null; ptz?: boolean; monitored?: boolean; driverInstanceId?: string | null;
  edgeId?: string | null; driverInstanceName?: string | null;
  timeCorrection?: { enabled?: boolean; maxDriftSeconds?: number; lastCorrectionAt?: string } | null;
  timeSyncSupported?: boolean;
  externalId?: string | null; externalSource?: string | null; replacedByDeviceId?: string | null;
  // REAL pass/fail checks from the live probes + device-event bridge.
  checks: Array<{ label: string; state: 'ok' | 'fail' }>;
  // REAL vision-probe numbers (cameras only; null until probed).
  videoQuality: { imageQuality: number | null; blur: number | null; recording: number | null; sceneStability: number | null; noSignal: boolean } | null;
  parent: DeviceRefLite | null;
  children: DeviceRefLite[];
  retention: { minDays: number; maxDays: number; enforce: boolean } | null;
  warranty: { expiryDate?: string | null; provider?: string | null; notes?: string | null } | null;
  maintenance: { nextDueDate?: string | null; intervalDays?: number | null; lastDoneDate?: string | null; notes?: string | null } | null;
  deductions: Array<{ event: string; label: string; penalty: number }>;
}
export interface Alarm { id: string; severity: string; category: string; type: string; site: string; device: string; timestamp: string; status: string; assignedUser: string | null; acknowledgedBy?: string | null; acknowledgedAt?: string | null; }
export interface RecordingChannel { deviceId: string; deviceName: string; channelId: number; site: string; recordingStatus: string; expectedRetention: number; actualRetention: number; gaps: number; archiveVerified: boolean; storagePercent: number; healthScore: number; }
export interface Firmware { items: Array<{ deviceId: string; deviceName: string; vendor: string; model: string; current: string | null; latest: string | null; upToDate: boolean | null; risk: string; site: string }>; compliance: { total: number; known: number; latest: number; needsUpgrade: number; unknown: number }; }
export interface ReportSchedule {
  id: string; name: string; reportType: string; format: 'pdf' | 'excel';
  cadence: 'daily' | 'weekly' | 'monthly'; recipients: string[]; enabled: boolean; lastRunAt: string | null;
}
export const reportSchedulesApi = {
  list: async () => (await api.get<{ data: ReportSchedule[] }>('/api/v1/report-schedules')).data.data,
  create: async (input: { name: string; reportType: string; format: 'pdf' | 'excel'; cadence: 'daily' | 'weekly' | 'monthly'; recipients: string[] }) =>
    (await api.post('/api/v1/report-schedules', input)).data,
  update: async (id: string, patch: Partial<{ name: string; reportType: string; enabled: boolean; cadence: string; format: string; recipients: string[] }>) =>
    (await api.patch(`/api/v1/report-schedules/${id}`, patch)).data,
  remove: async (id: string) => (await api.delete(`/api/v1/report-schedules/${id}`)).data,
  // Manual "send now": server generates the report and emails it to the given
  // recipients (branded HTML body + file attached).
  send: async (input: { reportType: string; format: 'pdf' | 'excel'; recipients: string[] }) =>
    (await api.post<{ data: { sent: number; failed: number; recipients: number } }>('/api/v1/reports/send', input)).data.data,
};

export interface FirmwareCatalogEntry { id: string; vendor: string; model: string; latestVersion: string; notes: string | null }
export const firmwareCatalogApi = {
  list: async () => (await api.get<{ data: FirmwareCatalogEntry[] }>('/api/v1/firmware-catalog')).data.data,
  create: async (input: { vendor: string; model: string; latestVersion: string; notes?: string }) =>
    (await api.post('/api/v1/firmware-catalog', input)).data,
  update: async (id: string, patch: Partial<{ vendor: string; model: string; latestVersion: string; notes: string | null }>) =>
    (await api.patch(`/api/v1/firmware-catalog/${id}`, patch)).data,
  remove: async (id: string) => (await api.delete(`/api/v1/firmware-catalog/${id}`)).data,
};
export interface TopologyNode {
  id: string; name: string; tier: string; coreType?: string; ptz?: boolean;
  parent?: string | null; status: string; poe: number | null; bandwidth: number | null; ports: string | null;
  // Topology metadata (cameras): connection (ip/analog), camera type, IP + port.
  connectionType?: 'ip' | 'analog' | 'unknown';
  cameraType?: 'ptz' | 'fixed' | 'unknown';
  cameraIp?: string | null;
  port?: number | null;
  // Topology metadata (recorders): the NVR/DVR/VMS IP address(es).
  ipAddresses?: string[];
}
export interface Topology { nodes: TopologyNode[]; discovered?: boolean; }
export interface AiHealth { appliances: Array<{ id: string; name: string; type: string; gpuUtil: number | null; inferenceRate: number | null; modelStatus: string; availability: number }>; analytics: Array<{ name: string; status: string; availability: number; live?: boolean }>; connected?: boolean; }
export interface Provider { kpis: { managedCustomers: number; managedDevices: number; activeIncidents: number; slaPerformance: number; monthlyHealthTrend: number[] }; customerRanking: Array<{ customer: string; score: number; devices: number }>; riskAnalysis: Array<{ label: string; pointsLost: number }>; technicianWorkload: Array<{ tech: string; open: number; resolved7d: number }>; }
export interface Executive { overallHealth: number; cameraAvailability: number; recordingHealth: number; openIssues: number; slaCompliance: number; trend: Array<{ t: string; score: number }>; reports: Array<{ title: string; type: string }>; }

// ---- ITSM (§23–26) ----
export interface ServiceTicket {
  id: string; ticketId: string; title: string; type: string; priority: string; severity: string;
  status: string; site: string; customer: string; relatedDevices: string[];
  alarmId: string | null; incidentId: string | null; slaSource: string | null;
  technician: string | null; vendor: string | null;
  firstResponseMins: number | null; resolutionMins: number | null; slaBreached: boolean; escalationLevel: number; createdAt: string;
  allowedTransitions?: string[];
}
export interface AutomationRule { id: string; name: string; trigger: string; action: string; enabled: boolean; }

// ---- Ticket Details (full operational history) ----
export interface ActivityLocation { site: string; building: string; floor: string; room: string }
export interface TicketActivity {
  id: string; seq: number; ts: string;
  actor: { name: string; role: string; company: string };
  kind: 'manual' | 'system';
  type: string; action: string;
  location: ActivityLocation | null; device: string | null;
  statusBefore: string | null; statusAfter: string | null;
  timeSpentMins: number | null; notes: string; attachments: string[]; followUp: boolean;
  internal: boolean;
}
export interface TicketWorkLog { id: string; ts: string; technician: string; summary: string; minutes: number }
export interface TicketComm { id: string; ts: string; author: string; role: string; channel: string; direction: string; internal: boolean; body: string }
export interface TicketAttachment { id: string; name: string; kind: string; uploadedBy: string; ts: string; sizeKb: number; thumb: string | null }
export interface TicketResolution { resolvedBy: string; resolvedAt: string; resolutionMins: number | null; rootCause: string; resolutionSummary: string; preventiveAction: string }
export interface TicketAuditEntry { id: string; ts: string; user: string; action: string; field: string; from: string; to: string }
export interface TicketStatusSla {
  status: string; slaSource: string | null; escalationLevel: number; breached: boolean; state: string;
  responseTarget: number | null; responseActual: number | null; resolutionTarget: number | null; resolutionActual: number | null;
}
export interface RelatedAsset { id: string | null; name: string; category: string; status: string; healthScore: number | null; location: ActivityLocation | null }
export interface TicketDetail {
  ticket: ServiceTicket & { description: string; updatedAt: string };
  statusSla: TicketStatusSla;
  relatedAssets: { devices: RelatedAsset[]; alarmId: string | null; incidentId: string | null };
  activities: TicketActivity[];
  workLogs: TicketWorkLog[]; totalMinutes: number;
  communications: TicketComm[];
  attachments: TicketAttachment[];
  resolution: TicketResolution | null;
  audit: TicketAuditEntry[];
}
export interface SlaRow { ticketId: string; title: string; customer: string; site: string; slaSource: string | null; responseTarget: number | null; responseActual: number | null; resolutionTarget: number | null; resolutionActual: number | null; breached: boolean; escalationLevel: number; state: string; }
export interface Escalations { matrix: Array<{ level: number; role: string; triggerAfter: string; notify: string }>; active: Array<{ ticketId: string; title: string; site: string; customer: string; level: number; reason: string; assignedTo: string | null; since: string }>; }
export interface VendorPerf { vendor: string; avgResponseMins: number; avgResolutionMins: number; slaCompliance: number; ticketVolume: number; repeatFailureRate: number; }

// ---- Video Assurance (§7.8–7.9) ----
export interface VideoAssuranceSummary {
  globalScore: number;
  checkScores: { heartbeat: number; activeCamera: number; recording: number; timeSync: number; retention: number; imageQuality: number; blur: number };
  distribution: { healthy: number; warning: number; critical: number };
  siteHeatmap: Array<{ siteId: string; siteName: string; score: number }>;
  customerScores: Array<{ id: string; name: string; score: number }>;
}
export interface VaCamera {
  cameraId: string; name: string; site: string; siteId: string; customer: string;
  vaScore: number; status: 'healthy' | 'warning' | 'critical';
  heartbeat: number; activeCamera: number; recording: number; timeSync: number; retention: number;
  imageQuality: number; blurScore: number; frozen: boolean; sceneStable: boolean;
}
export interface VaContribution { check: string; label: string; weight: number; subScore: number; weighted: number }
export interface VaCameraDetail {
  cameraId: string; name: string; site: string; vendor: string; model: string;
  ptz?: boolean;
  vaScore: number; status: string;
  contributions: VaContribution[];
  panel: { heartbeat: boolean; recording: boolean; retention: boolean; timeSync: boolean; imageQuality: boolean };
  imageQuality: { focus: number; blur: number; exposure: number; obstruction: number; sceneStability: number };
  baselineSnapshot: string; currentSnapshot: string;
  findings: string[]; probableCause: string;
  trend: Array<{ t: string; score: number }>;
}
// Generic image-quality analysis — Core-5 detectors (§7.8).
export type IqDetectorKey = 'blur' | 'frozen' | 'scene' | 'obstruction' | 'exposure' | 'nosignal';
export interface IqDetector { key: IqDetectorKey; label: string; critical: number; warning: number; avg: number }
export interface IqCameraRow {
  cameraId: string; name: string; site: string; ptz?: boolean;
  scores: Record<IqDetectorKey, number>;
  worst: { detector: string; key: IqDetectorKey; score: number };
}
export interface ImageQualityAnalysis { detectors: IqDetector[]; cameras: IqCameraRow[] }
export type RecState = 'ok' | 'partial' | 'missing';
export interface RecordingVerification {
  range?: string; slots?: number;
  coveragePercent: number; verifiedCount: number; missingCount: number; total: number;
  cameras: Array<{ cameraId: string; name: string; site: string; siteId?: string | null; recordingScore: number; verified: boolean; timeline: Array<{ hour: number; state: RecState }> }>;
}
export interface RetentionCompliance {
  cameras: Array<{ cameraId: string; name: string; site: string; actualDays: number; minPolicy: number; maxPolicy: number; storagePercent: number; compliant: boolean; violation: string | null }>;
  forecast: Array<{ day: string; usedPercent: number }>;
}
export type PlaybackState = 'ok' | 'missing' | 'lowfps';
export type PlaybackVerification = Array<{ cameraId: string; name: string; site: string; recordingScore: number; verificationPoint: string; timeline: Array<{ hour: number; state: PlaybackState }> }>;
export interface NocWallboard {
  overallHealth: number; videoAssurance: number; recordingAssurance: number; blurDetection: number; retentionCompliance: number;
  distribution: { healthy: number; warning: number; critical: number };
  sites: Array<{ siteId: string; siteName: string; score: number }>;
}

// ---- Asset trees (physical driver / logical sites / tag-groups) ----
export type TreeNodeType =
  | 'organization' | 'site' | 'building' | 'floor' | 'zone'
  | 'group' | 'device' | 'driver' | 'unassigned';
export interface TreeNode {
  id: string; name: string; type: TreeNodeType; coreType?: string;
  deviceCount: number; score: number; status: 'healthy' | 'warning' | 'critical';
  children: TreeNode[];
  parentDeviceId?: string | null;
  placedAt?: string | null; // site name a camera is individually placed in
  physicalRef?: { type: string; id: string };
}
// physical = driver→device→channels; logical = sites hierarchy; groups = tag-groups.
export interface LocationTree { physical: TreeNode[]; logical: TreeNode[]; groups: TreeNode[] }

export const locationsApi = {
  tree: get<LocationTree>('/api/v1/locations/tree'),
};

// ---- Physical hierarchy (buildings/floors/zones) ----
export interface Building { id: string; siteId: string; name: string; address?: string | null; latitude?: number | null; longitude?: number | null }
export interface Floor { id: string; buildingId: string; name: string; level: number; width?: number | null; height?: number | null; hasPlan?: boolean }
export interface Zone { id: string; floorId: string; name: string; type: string }
export interface FloorNavLink { id: string; x: number; y: number; targetFloorId: string; label?: string }
export interface FloorPlan { id: string; dataUrl: string | null; width: number | null; height: number | null; navLinks?: FloorNavLink[] }
export type LocationKind = 'site' | 'building' | 'floor' | 'zone';
/** What deleting a location takes with it. Devices are unplaced, never deleted. */
export interface LocationImpact { buildings: number; floors: number; zones: number; devices: number }
export const hierarchyApi = {
  buildings: (siteId?: string) =>
    get<Building[]>(`/api/v1/buildings${siteId ? `?siteId=${siteId}` : ''}`)(),
  floors: (buildingId?: string) =>
    get<Floor[]>(`/api/v1/floors${buildingId ? `?buildingId=${buildingId}` : ''}`)(),
  zones: (floorId?: string) =>
    get<Zone[]>(`/api/v1/zones${floorId ? `?floorId=${floorId}` : ''}`)(),
  createBuilding: async (siteId: string, name: string): Promise<Building> =>
    (await api.post<{ data: Building }>('/api/v1/buildings', { siteId, name })).data.data,
  // Set a building's own GIS coordinates / name / address (map drag-to-place).
  updateBuilding: async (id: string, patch: { name?: string; address?: string | null; latitude?: number | null; longitude?: number | null }): Promise<Building> =>
    (await api.patch<{ data: Building }>(`/api/v1/buildings/${id}`, patch)).data.data,
  createFloor: async (buildingId: string, name: string, level = 0): Promise<Floor> =>
    (await api.post<{ data: Floor }>('/api/v1/floors', { buildingId, name, level })).data.data,
  createZone: async (floorId: string, name: string, type = 'area'): Promise<Zone> =>
    (await api.post<{ data: Zone }>('/api/v1/zones', { floorId, name, type })).data.data,
  // Floor-plan image (digital twin background).
  floorPlan: async (floorId: string): Promise<FloorPlan> =>
    (await api.get<{ data: FloorPlan }>(`/api/v1/floors/${floorId}/plan`)).data.data,
  setFloorPlan: async (floorId: string, dataUrl: string, width?: number, height?: number) =>
    (await api.put(`/api/v1/floors/${floorId}/plan`, { dataUrl, width, height })).data,
  removeFloorPlan: async (floorId: string) =>
    (await api.delete<{ data: { id: string; hasPlan: boolean } }>(`/api/v1/floors/${floorId}/plan`)).data.data,
  // Hierarchy deletes. `impact` previews the cascade (children removed, devices
  // unplaced) so the confirmation can state it rather than guess.
  locationImpact: async (kind: LocationKind, id: string): Promise<LocationImpact> =>
    (await api.get<{ data: LocationImpact }>(`/api/v1/locations/${kind}/${id}/impact`)).data.data,
  deleteLocation: async (kind: LocationKind, id: string) =>
    (await api.delete<{ data: LocationImpact & { id: string; deleted: boolean } }>(
      `/api/v1/${kind}s/${id}`,
    )).data.data,
  // Inter-floor navigation hotspots (normalized 0–1 coords → target floor).
  setFloorNavLinks: async (floorId: string, navLinks: FloorNavLink[]) =>
    (await api.patch<{ data: { id: string; navLinks: FloorNavLink[] } }>(`/api/v1/floors/${floorId}/nav-links`, { navLinks })).data.data,
};

// ---- Device placement (into the sites hierarchy) ----
export type PlacementTarget = {
  organizationId?: string | null; siteId?: string | null;
  buildingId?: string | null; floorId?: string | null; zoneId?: string | null;
};
export const placementApi = {
  place: async (deviceId: string, target: PlacementTarget) =>
    (await api.put<{ data: unknown }>(`/api/v1/devices/${deviceId}/placement`, target)).data.data,
  unplace: async (deviceId: string) =>
    (await api.put<{ data: unknown }>(`/api/v1/devices/${deviceId}/placement`, {})).data.data,
  // Enable/disable monitoring for a camera/recorder (cascades to NVR channels).
  setMonitored: async (deviceId: string, monitored: boolean) =>
    (await api.patch<{ data: unknown }>(`/api/v1/devices/${deviceId}/monitored`, { monitored })).data.data,
  // Enable/disable optional auto time-correction (per-device).
  setTimeCorrection: async (deviceId: string, body: { enabled: boolean; maxDriftSeconds?: number }) =>
    (await api.patch<{ data: unknown }>(`/api/v1/devices/${deviceId}/time-correction`, body)).data.data,
  // Replace hardware: adopt oldId's identity (placement/history/children) onto a
  // new (already-synced) unit. Identify the new unit by id or serial.
  replaceHardware: async (oldId: string, body: { newDeviceId?: string; newSerialNumber?: string }) =>
    (await api.post<{ data: unknown }>(`/api/v1/devices/${oldId}/replace`, body)).data.data,
  // Update warranty / preventive-maintenance metadata.
  setMetadata: async (deviceId: string, body: {
    warranty?: { expiryDate?: string | null; provider?: string | null; notes?: string | null } | null;
    maintenance?: { nextDueDate?: string | null; intervalDays?: number | null; lastDoneDate?: string | null; notes?: string | null } | null;
  }) => (await api.patch<{ data: unknown }>(`/api/v1/devices/${deviceId}/metadata`, body)).data.data,
  // Device logs collected by the edge log collector (newest first).
  logs: async (deviceId: string, limit = 200): Promise<DeviceLogEntry[]> =>
    (await api.get<{ data: DeviceLogEntry[] }>(`/api/v1/devices/${deviceId}/logs?limit=${limit}`)).data.data,
  // Spatial coordinates: geo (map) and/or indoorX/Y normalized 0–1 (floor twin).
  setCoordinates: async (deviceId: string, coords: { latitude?: number | null; longitude?: number | null; indoorX?: number | null; indoorY?: number | null }) =>
    (await api.put<{ data: InventoryDevice }>(`/api/v1/devices/${deviceId}/coordinates`, coords)).data.data,
};

export interface DeviceLogEntry {
  id: string; source: 'nvr' | 'syslog' | 'driver';
  level: 'info' | 'warning' | 'error' | 'critical';
  message: string; eventTime: string | null; receivedAt: string;
}

// ---- Logical tag-groups ----
export interface LogicalGroup {
  id: string; name: string; description: string | null; parentId: string | null;
  type: string; color: string | null;
}
export const logicalGroupsApi = {
  list: get<LogicalGroup[]>('/api/v1/logical-groups'),
  create: async (input: { name: string; type?: string; color?: string; parentId?: string | null }): Promise<LogicalGroup> =>
    (await api.post<{ data: LogicalGroup }>('/api/v1/logical-groups', input)).data.data,
  remove: async (id: string) => { await api.delete(`/api/v1/logical-groups/${id}`); },
  addMember: async (groupId: string, physicalType: string, physicalId: string) =>
    (await api.post(`/api/v1/logical-groups/${groupId}/members`, { physicalType, physicalId })).data,
  removeMember: async (groupId: string, physicalType: string, physicalId: string) => {
    await api.delete(`/api/v1/logical-groups/${groupId}/members`, { data: { physicalType, physicalId } });
  },
};

// ---- Real, DB-backed sites + organizations (tenant-scoped) ----
export interface Site {
  id: string; tenantId: string; organizationId: string; name: string;
  address: string | null; timezone: string | null;
  latitude: number | null; longitude: number | null;
  createdAt: string; updatedAt: string;
}
export interface Organization { id: string; name: string; parentId: string | null; level: number }
export interface CreateSiteInput {
  name: string; address?: string; timezone?: string; organizationId?: string;
}

export const sitesApi = {
  list: get<Site[]>('/api/v1/sites'),
  organizations: get<Organization[]>('/api/v1/organizations'),
  create: async (input: CreateSiteInput): Promise<Site> =>
    (await api.post<{ data: Site }>('/api/v1/sites', input)).data.data,
  // Set a site's coordinates / address (the map's click-place + geocode editor).
  update: async (id: string, patch: { name?: string; address?: string | null; latitude?: number | null; longitude?: number | null }): Promise<Site> =>
    (await api.patch<{ data: Site }>(`/api/v1/sites/${id}`, patch)).data.data,
};

// OSM Nominatim geocode (server-side proxy).
export interface GeoHit { lat: number | null; lon: number | null; displayName: string | null }
export const geocodeApi = {
  // First/best match (used by the place-from-address button).
  lookup: async (q: string): Promise<GeoHit> =>
    (await api.get<{ data: GeoHit }>(`/api/v1/geocode?q=${encodeURIComponent(q)}`)).data.data,
  // Candidate list for the address-search dropdown.
  search: async (q: string): Promise<GeoHit[]> =>
    (await api.get<{ data: { results: GeoHit[] } }>(`/api/v1/geocode?q=${encodeURIComponent(q)}`)).data.data.results ?? [],
};

// ---- Device onboarding (drivers: add a real NVR/DVR/VMS/camera) ----
export interface DriverCatalogEntry {
  slug: string; name: string; category: string; vendor?: string;
  version?: string; description?: string; capabilities: string[];
  configSchema: Record<string, unknown>;
}
export interface DiscoveredDevice {
  id: string; name: string; category: string; coreType: string;
  serialNumber: string | null; status: string; channel: number | null;
  parentDeviceId: string | null;
}
export const driversApi = {
  catalog: get<DriverCatalogEntry[]>('/api/v1/drivers'),
  testConnection: async (driverSlug: string, config: Record<string, unknown>) =>
    (await api.post<{ data: { success: boolean; note?: string; error?: string } }>(
      '/api/v1/driver-instances/test-connection', { driverSlug, config },
    )).data.data,
  createInstance: async (input: { name: string; driverSlug: string; config: Record<string, unknown>; edgeId?: string | null }) =>
    (await api.post<{ data: { id: string } }>('/api/v1/driver-instances', input)).data.data,
  start: async (id: string) =>
    (await api.post<{ data: { started: boolean; status: string; deviceCount: number } }>(
      `/api/v1/driver-instances/${id}/start`, {},
    )).data.data,
  discover: async (id: string) =>
    (await api.post<{ data: { deviceCount: number; devices: DiscoveredDevice[] } }>(
      `/api/v1/driver-instances/${id}/discover`, {},
    )).data.data,
};

// Build a query string from a params record (drops empty values).
const qs = (params?: Record<string, string>) => {
  const e = Object.entries(params ?? {}).filter(([, v]) => v);
  return e.length ? `?${new URLSearchParams(Object.fromEntries(e)).toString()}` : '';
};
const getP = <T>(path: string) => async (params?: Record<string, string>): Promise<T> =>
  (await api.get<{ data: T }>(`${path}${qs(params)}`)).data.data;

/** A known edge node, for the "Run on edge" select in Add Device. */
export interface EdgeOption { edgeId: string; name: string; status: string; connected: boolean }
export const edgesApi = {
  /** List this tenant's edges (id, name, live status) for assignment selects.
   *  Always returns an array — the endpoint wraps the list in {data, meta}, so
   *  guard against any non-array body to avoid a render crash downstream. */
  list: async (): Promise<EdgeOption[]> => {
    const body = (await api.get<{ data: EdgeOption[] }>('/api/v1/edges')).data;
    return Array.isArray(body?.data) ? body.data : [];
  },
};

export const videoAssuranceApi = {
  summary: getP<VideoAssuranceSummary>('/api/v1/video-assurance/summary'),
  cameras: getP<VaCamera[]>('/api/v1/video-assurance/cameras'),
  camera: async (id: string) => (await api.get<{ data: VaCameraDetail }>(`/api/v1/video-assurance/cameras/${id}`)).data.data,
  imageQuality: getP<ImageQualityAnalysis>('/api/v1/video-assurance/image-quality-analysis'),
  recordingVerification: async (params?: Record<string, string>) =>
    (await api.get<{ data: RecordingVerification }>(`/api/v1/video-assurance/recording-verification${qs(params)}`)).data.data,
  retention: getP<RetentionCompliance>('/api/v1/video-assurance/retention'),
  playback: getP<PlaybackVerification>('/api/v1/video-assurance/playback'),
  noc: get<NocWallboard>('/api/v1/noc/wallboard'),
};

export interface SubscriptionView {
  plan: string | null; status: string | null;
  limits: Record<string, number>;
  usage: { devices: number; cameras: number; edges: number; users: number };
  overLimit: Array<'cameras' | 'devices' | 'users'>;
}
export const subscriptionApi = {
  get: get<SubscriptionView>('/api/v1/subscription'),
};

export interface TotpStatus { enabled: boolean; enforced: boolean }
export interface TotpEnroll { secret: string; otpauth: string; qrDataUrl: string }
export const twoFactorApi = {
  status: get<TotpStatus>('/api/v1/auth/2fa/status'),
  enroll: async (): Promise<TotpEnroll> => (await api.post<{ data: TotpEnroll }>('/api/v1/auth/2fa/enroll', {})).data.data,
  confirm: async (code: string) => (await api.post<{ data: { enabled: boolean } }>('/api/v1/auth/2fa/confirm', { code })).data.data,
  disable: async () => { await api.post('/api/v1/auth/2fa/disable', {}); },
};

// Account-security authenticator, bound to the user's LOGTO account (the factor
// that actually gates the Logto login MFA). Distinct from twoFactorApi above,
// which is the legacy identity-table TOTP. setup returns a secret + a
// server-rendered QR data-URL; confirm binds it only after a valid code.
export interface MfaStatus { hasTotp: boolean; factors: Array<{ id: string; type: string }> }
export interface MfaSetup { secret: string; qrCode: string }
export const mfaApi = {
  status: get<MfaStatus>('/api/v1/auth/mfa/status'),
  setup: async (): Promise<MfaSetup> => (await api.post<{ data: MfaSetup }>('/api/v1/auth/mfa/totp/setup')).data.data,
  confirm: async (code: string) => (await api.post<{ data: { added: boolean } }>('/api/v1/auth/mfa/totp/confirm', { code })).data.data,
  remove: async (id: string) => { await api.delete(`/api/v1/auth/mfa/totp/${encodeURIComponent(id)}`); },
};

/** Alarm operator actions — acknowledge + assign. Alarms are derived live from
 *  device faults; the server persists this state per device+event. */
export const alarmsApi = {
  acknowledge: async (id: string) => (await api.post(`/api/v1/alarms/${encodeURIComponent(id)}/acknowledge`, {})).data,
  assign: async (id: string, input: { userId?: string; name?: string }) =>
    (await api.post(`/api/v1/alarms/${encodeURIComponent(id)}/assign`, input)).data,
};

export const inventoryApi = {
  list: getP<InventoryDevice[]>('/api/v1/inventory'),
  detail: async (id: string) => (await api.get<{ data: DeviceDetail }>(`/api/v1/inventory/${id}`)).data.data,
  alarms: getP<Alarm[]>('/api/v1/alarms'),
  recording: getP<RecordingChannel[]>('/api/v1/recording'),
  firmware: get<Firmware>('/api/v1/firmware'),
  topology: get<Topology>('/api/v1/topology'),
  aiHealth: get<AiHealth>('/api/v1/ai-health'),
  provider: get<Provider>('/api/v1/provider'),
  executive: get<Executive>('/api/v1/executive'),
  // ITSM
  serviceTickets: get<ServiceTicket[]>('/api/v1/service-tickets'),
  ticketDetail: async (id: string) => (await api.get<{ data: TicketDetail }>(`/api/v1/service-tickets/${id}/detail`)).data.data,
  automationRules: get<AutomationRule[]>('/api/v1/ticket-automation'),
  slaTracking: get<SlaRow[]>('/api/v1/sla-tracking'),
  escalations: get<Escalations>('/api/v1/escalations'),
  vendorPerformance: get<VendorPerf[]>('/api/v1/vendor-performance'),
};

// Per-user preferences (server-side, per user+tenant).
export interface UserPreferences {
  notificationChannels: Record<string, boolean>;
  theme: string; soundEnabled: boolean; desktopNotifications: boolean;
  language: string;
  uiSettings: Record<string, unknown>;
}
export const userPrefsApi = {
  get: async () => (await api.get<{ data: UserPreferences }>('/api/v1/user-preferences')).data.data,
  setNotificationChannels: async (notificationChannels: Record<string, boolean>) =>
    (await api.put('/api/v1/user-preferences', { notificationChannels })).data,
  setLanguage: async (language: string) =>
    (await api.put('/api/v1/user-preferences', { uiSettings: { language } })).data,
};

// Monitoring schedules (F2) — per-scope/per-check health-check intervals.
export interface MonitoringSchedule {
  id: string; scope: string; scopeId: string | null; checkType: string; intervalMs: number; enabled: boolean;
}
export const monitoringSchedulesApi = {
  list: async () => (await api.get<{ data: MonitoringSchedule[] }>('/api/v1/monitoring-schedules')).data.data,
  create: async (input: { scope: string; scopeId?: string; checkType?: string; intervalMs: number }) =>
    (await api.post('/api/v1/monitoring-schedules', input)).data,
  update: async (id: string, patch: Partial<{ checkType: string; intervalMs: number; enabled: boolean }>) =>
    (await api.patch(`/api/v1/monitoring-schedules/${id}`, patch)).data,
  remove: async (id: string) => (await api.delete(`/api/v1/monitoring-schedules/${id}`)).data,
};

// Ticket automation rules (tenant system config) — CRUD on routing_rules.
export interface AutomationRule { id: string; name: string; trigger: string; action: string; enabled: boolean; priority?: number }
export const automationRulesApi = {
  list: async () => (await api.get<{ data: AutomationRule[] }>('/api/v1/automation-rules')).data.data,
  create: async (input: { name: string; trigger?: string; action?: string; enabled?: boolean }) =>
    (await api.post('/api/v1/automation-rules', input)).data,
  update: async (id: string, patch: Partial<{ name: string; trigger: string; action: string; enabled: boolean }>) =>
    (await api.patch(`/api/v1/automation-rules/${id}`, patch)).data,
  remove: async (id: string) => (await api.delete(`/api/v1/automation-rules/${id}`)).data,
};

// ── Rules & Automation engine (real trigger→condition→action over `rules`). ──
export interface RuleCondition { field: string; op: 'eq' | 'ne' | 'in' | 'contains' | 'gt' | 'lt'; value: unknown }
export interface RuleActionDef { type: 'notify' | 'ticket.create' | 'ticket.escalate' | 'ticket.assign' | 'webhook' | 'suppress' | 'notify.operators' | 'sound.play' | 'speak' | 'incident.resolve' | 'device.maintenance' | 'note.add'; params?: Record<string, unknown> }
export interface RuleTriggerDef { type: 'incident.opened' | 'incident.resolved' | 'device.status' | 'sla.breach' | 'sla.at_risk' | 'schedule'; cron?: string; intervalMinutes?: number }
export interface AutomationRuleFull {
  id: string; name: string; description: string | null; enabled: boolean; priority: number;
  cooldownMs: number | null; trigger: RuleTriggerDef; conditions: RuleCondition[]; actions: RuleActionDef[];
  triggerCount: number; lastTriggeredAt: string | null;
}
export interface CatalogOption { value: string; label: string }
export interface CatalogField { path: string; label: string; valueSource: string }
export interface CatalogParam { key: string; label: string; optionsSource?: string; kind?: 'text' | 'number'; optional?: boolean }
export interface AutomationCatalog {
  triggers: Array<{ type: string; label: string; fields: CatalogField[]; schedule?: boolean }>;
  operators: Array<{ op: string; label: string }>;
  actions: Array<{ type: string; label: string; params: CatalogParam[] }>;
  options: Record<string, CatalogOption[]>;
}
export interface RuleInput {
  name: string; description?: string; enabled?: boolean; priority?: number; cooldownMs?: number | null;
  trigger: RuleTriggerDef; conditions?: RuleCondition[]; actions: RuleActionDef[];
}
export const automationApi = {
  catalog: get<AutomationCatalog>('/api/v1/automation/catalog'),
  list: get<AutomationRuleFull[]>('/api/v1/automation/rules'),
  create: async (input: RuleInput) => (await api.post<{ data: AutomationRuleFull }>('/api/v1/automation/rules', input)).data.data,
  update: async (id: string, patch: Partial<RuleInput>) => (await api.patch(`/api/v1/automation/rules/${id}`, patch)).data,
  remove: async (id: string) => { await api.delete(`/api/v1/automation/rules/${id}`); },
  test: async (id: string) => (await api.post<{ data: { matched: boolean; actions: Array<{ type: string; ok: boolean; detail?: string }> } }>(`/api/v1/automation/rules/${id}/test`, {})).data.data,
};

// Retention policies — per-scope min/max retention days (+ opt-in enforce).
export interface RetentionPolicy {
  id: string; scope: string; scopeId: string | null;
  minRetentionDays: number; maxRetentionDays: number; enforce: boolean; enabled: boolean;
}
export const retentionPoliciesApi = {
  list: async () => (await api.get<{ data: RetentionPolicy[] }>('/api/v1/retention-policies')).data.data,
  create: async (input: { scope: string; scopeId?: string; minRetentionDays: number; maxRetentionDays: number; enforce?: boolean }) =>
    (await api.post('/api/v1/retention-policies', input)).data,
  update: async (id: string, patch: Partial<{ minRetentionDays: number; maxRetentionDays: number; enforce: boolean; enabled: boolean }>) =>
    (await api.patch(`/api/v1/retention-policies/${id}`, patch)).data,
  remove: async (id: string) => (await api.delete(`/api/v1/retention-policies/${id}`)).data,
};

// DATA retention (CFG-03) — the DB-row TTLs the maintenance job PERMANENTLY
// DELETES by (device events / alarms / incidents / audit). Distinct from
// retentionPoliciesApi above, which is NVR *video recording* days.
export type RetentionField = 'eventRetentionDays' | 'alarmRetentionDays' | 'incidentRetentionDays' | 'auditRetentionDays';
export type RetentionEnabledField = 'eventRetentionEnabled' | 'alarmRetentionEnabled' | 'incidentRetentionEnabled' | 'auditRetentionEnabled';
/** Each category carries its TTL (`days`), an ON/OFF flag (`enabled`), and a
 *  human description (`governs`). A disabled category is never auto-deleted. */
export type DataRetention = Record<RetentionField, { days: number; enabled: boolean; governs: string }>;
/** Shape of the 409 the API returns when a change would delete rows (shortening
 *  an enabled TTL, or enabling a category that already has rows past cutoff). */
export interface DataLossImpact {
  impact: Record<string, { from: number; to: number; enabling?: boolean; rowsDeleted: number }>;
}
export const dataRetentionApi = {
  get: async () => (await api.get<{ data: { retention: DataRetention; note: string } }>('/api/v1/data-retention')).data.data,
  // A change that deletes rows (shorten an enabled TTL, or enable a category with
  // rows past cutoff) requires confirmDataLoss — the server 409s otherwise and
  // reports how many rows it would delete.
  update: async (patch: Partial<Record<RetentionField, number>> & Partial<Record<RetentionEnabledField, boolean>> & { confirmDataLoss?: boolean }) =>
    (await api.patch<{ data: { retention: DataRetention } }>('/api/v1/data-retention', patch)).data.data,
};

// Auto-create ASSETS from discovered devices (opt-in per tenant). When enabled,
// each driver sync creates/refreshes a lifecycle asset for every device in an
// allowed category. availableCategories are the device categories that can
// produce an asset (the chip options).
export interface AssetAutomation {
  enabled: boolean;
  categories: string[];
  availableCategories: string[];
}
export const assetAutomationApi = {
  get: async () => (await api.get<{ data: AssetAutomation }>('/api/v1/asset-automation')).data.data,
  update: async (patch: { enabled?: boolean; categories?: string[] }) =>
    (await api.patch<{ data: AssetAutomation }>('/api/v1/asset-automation', patch)).data.data,
};

// Notification RECIPIENTS — channel CREDENTIALS + message templates are now
// platform-global (super-admin Control Plane). Here a tenant admin sets only the
// per-channel destination lists; the list is scoped to the globally-enabled
// channels the platform exposes.
export interface NotificationRecipientChannel {
  channel: string; targets: string[]; enabled: boolean;
}
export const notificationRecipientsApi = {
  list: async () => (await api.get<{ data: { channels: NotificationRecipientChannel[] } }>('/api/v1/notification-recipients')).data.data.channels,
  save: async (channel: string, input: { targets: string[]; enabled?: boolean }) =>
    (await api.put(`/api/v1/notification-recipients/${channel}`, input)).data,
  test: async (channel: string) =>
    (await api.post<{ data: { ok: boolean; sent: number; failed: number; noRecipients?: boolean; error?: string } }>(`/api/v1/notification-recipients/${channel}/test`, {})).data.data,
};
